import React, { createContext, useContext, useEffect, useState } from 'react';
import { localStorageGetItem, localStorageSetItem } from '../storage/localStorageManager';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import useUser from '../../hooks/useSelector';
import { useAppDispatch } from '../../redux/reduxhooks';
import { signOut } from '../../redux/user';
import { routes } from '../../containers/routes/routes-names';
import { SignupTabs } from './SignupLayout';

type RegistrationContextType = {
    // State
    candidateType: string;
    signAs: string;
    checkIcon: string;
    authType: string;
    showLoginButtonType: boolean;
    showSelectAccountType: boolean;
    digioSuccessData: any;
    pageLoading: boolean;
    hasUnsavedChanges: boolean;
    KYCEmail: string;
    disableManagerTypeBtn: boolean;
    disableCandidateTypeBtn: boolean;
    rerenderKey: number;
    showOTPBtn: boolean;
    showPasswordTab: boolean;
    showNextTab: boolean;
    showCustomSinUpTab: boolean;
    unclaimedData?: any;
    activeTab: string;
    data: any;

    // Setters
    setCandidateType: (type: string) => void;
    setSignAs: (type: string) => void;
    setCheckIcon: (icon: string) => void;
    setAuthType: (type: string) => void;
    setShowLoginButtonType: (show: boolean) => void;
    setShowSelectAccountType: (show: boolean) => void;
    setDigioSuccessData: (data: any) => void;
    setPageLoading: (loading: boolean) => void;
    setHasUnsavedChanges: (value: boolean) => void;
    setKYCEmail: (email: string) => void;
    setDisableManagerTypeBtn: (disabled: boolean) => void;
    setDisableCandidateTypeBtn: (disabled: boolean) => void;
    setRerenderKey: (key: number) => void;
    setShowOTPBtn: (key: boolean) => void;
    setShowPasswordTab: (key: boolean) => void;
    setShowNextTab: (key: boolean) => void;
    setShowCustomSinUpTab: (key: boolean) => void;
    handleSelect: (key: string, key2: string, value: any) => void;
    setActiveTab: (tab: string) => void;
    setData: (value: any) => void;

    resetAllState: () => void;

    // IDs
    candidateId?: string;
    managerId?: string;
    ConfirmCandidateInvitationId?: any;
    ConfirmUnclaimedCandidateInvitationId?: any;
};

const RegistrationContext = createContext<RegistrationContextType | undefined>(undefined);

const getInitialRegistrationState = () => ({
    activeTab: 'select-account-type',
    unclaimedData: undefined,
    showUI: false,
    candidateType: '',
    authType: '',
    checkIcon: '',
    pageLoading: false,
    showLoginButtonType: false,
    showSelectAccountType: true,
    signAs: '',
    KYCEmail: '',
    digioSuccessData: null,
    hasUnsavedChanges: false,
    disableManagerTypeBtn: false,
    disableCandidateTypeBtn: false,
    rerenderKey: 0,
    showPasswordTab: false,
    showOTPBtn: true,
    showNextTab: false,
    showCustomSinUpTab: true,
    data: undefined,
});

export const useRegistration = () => {
    const context = useContext(RegistrationContext);
    if (!context) throw new Error('useRegistration must be used within RegistrationProvider');
    return context;
};

export const RegistrationProvider: React.FC<{
    children: React.ReactNode;
    candidateId?: string;
    managerId?: string;
}> = ({ children, candidateId, managerId }) => {
    const initial = getInitialRegistrationState();

    const [activeTab, setActiveTab] = useState(initial.activeTab);
    const [unclaimedData, setUnclaimedData] = useState<any>(initial.unclaimedData);
    const [showUI, setShowUI] = useState(initial.showUI);
    const [candidateType, setCandidateType] = useState(initial.candidateType);
    const [authType, setAuthType] = useState(initial.authType);
    const [checkIcon, setCheckIcon] = useState(initial.checkIcon);
    const [pageLoading, setPageLoading] = useState(initial.pageLoading);
    const [showLoginButtonType, setShowLoginButtonType] = useState(initial.showLoginButtonType);
    const [showSelectAccountType, setShowSelectAccountType] = useState(initial.showSelectAccountType);
    const [signAs, setSignAs] = useState(initial.signAs);
    const [KYCEmail, setKYCEmail] = useState(initial.KYCEmail);
    const [digioSuccessData, setDigioSuccessData] = useState<any>(initial.digioSuccessData);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(initial.hasUnsavedChanges);
    const [disableManagerTypeBtn, setDisableManagerTypeBtn] = useState(initial.disableManagerTypeBtn);
    const [disableCandidateTypeBtn, setDisableCandidateTypeBtn] = useState(initial.disableCandidateTypeBtn);
    const [rerenderKey, setRerenderKey] = useState(initial.rerenderKey);
    const [showPasswordTab, setShowPasswordTab] = useState(initial.showPasswordTab);
    const [showOTPBtn, setShowOTPBtn] = useState(initial.showOTPBtn);
    const [showNextTab, setShowNextTab] = useState(initial.showNextTab);
    const [showCustomSinUpTab, setShowCustomSinUpTab] = useState(initial.showCustomSinUpTab);
    const [data, setData] = useState(initial.data);

    const storageData = localStorageGetItem('unclaimed', false);
    const claimedType = localStorageGetItem('claimedType', true);

    const hasUser = useUser();
    const urlPath = useLocation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { ConfirmCandidateInvitationId, AffiliateReferral, ConfirmUnclaimedCandidateInvitationId } =
        queryString.parse(urlPath.search);

    useEffect(() => {
        if (ConfirmCandidateInvitationId || (showUI && unclaimedData && unclaimedData.claimedType === 'candidate')) {
            setCheckIcon('candidate');
            setCandidateType('candidate');
            setDisableManagerTypeBtn(true);
            setSignAs('candidate');
        }

        if (AffiliateReferral || (showUI && unclaimedData && unclaimedData.claimedType === 'company')) {
            setCheckIcon('company');
            setCandidateType('company');
            setSignAs('company');
            setDisableCandidateTypeBtn(true);
            if (AffiliateReferral) {
                localStorageSetItem('referral', AffiliateReferral, true);
            }

            if (unclaimedData.claimedType === 'company') {
                setActiveTab('select-auth-type');
                setShowLoginButtonType(true);
            }
        }

        if (ConfirmUnclaimedCandidateInvitationId) {
            setActiveTab(SignupTabs.BASIC_DETAILS);
            setCheckIcon('candidate');
            setCandidateType('candidate');
            setDisableManagerTypeBtn(true);
            setSignAs('candidate');
            setAuthType('customAuth');
            localStorageSetItem('unclaimedNewReg', ConfirmUnclaimedCandidateInvitationId, true);
        }

        if (hasUser && hasUser.suspendedStatus === 1) {
            dispatch(signOut(navigate));
        }
    }, [ConfirmCandidateInvitationId, AffiliateReferral, unclaimedData, ConfirmUnclaimedCandidateInvitationId]);

    useEffect(() => {
        if (claimedType == 'company') {
            setSignAs(claimedType);
            setCheckIcon('company');
            setCandidateType('company');
            setShowLoginButtonType(true);
        }
        if (storageData) {
            setUnclaimedData({ ...storageData, avatarUrl: '' });
            setShowUI(true);
        }
    }, []);

    const resetAllState = () => {
        const init = getInitialRegistrationState();
        setActiveTab(init.activeTab);
        if (!storageData) {
            setUnclaimedData(init.unclaimedData);
        }
        setShowUI(init.showUI);
        setCandidateType(init.candidateType);
        setAuthType(init.authType);
        setCheckIcon(init.checkIcon);
        setPageLoading(init.pageLoading);
        setShowLoginButtonType(init.showLoginButtonType);
        setShowSelectAccountType(init.showSelectAccountType);
        setSignAs(init.signAs);
        setKYCEmail(init.KYCEmail);
        setDigioSuccessData(init.digioSuccessData);
        setHasUnsavedChanges(init.hasUnsavedChanges);
        setDisableManagerTypeBtn(init.disableManagerTypeBtn);
        setDisableCandidateTypeBtn(init.disableCandidateTypeBtn);
        setRerenderKey(init.rerenderKey);
        setShowPasswordTab(init.showPasswordTab);
        setShowOTPBtn(init.showOTPBtn);
        setShowNextTab(init.showNextTab);
        setShowCustomSinUpTab(init.showCustomSinUpTab);
        setData(init.data);
    };

    return (
        <RegistrationContext.Provider
            value={{
                ConfirmUnclaimedCandidateInvitationId,
                ConfirmCandidateInvitationId,
                candidateType,
                unclaimedData,
                activeTab,
                setCandidateType,
                signAs,
                data,
                setData,
                setSignAs,
                checkIcon,
                setCheckIcon,
                authType,
                setShowNextTab,
                showNextTab,
                showOTPBtn,
                setShowPasswordTab,
                showPasswordTab,
                showCustomSinUpTab,
                setShowCustomSinUpTab,
                setShowOTPBtn,
                setAuthType,
                showLoginButtonType,
                setShowLoginButtonType,
                showSelectAccountType,
                setShowSelectAccountType,
                digioSuccessData,
                setDigioSuccessData,
                pageLoading,
                setPageLoading,
                hasUnsavedChanges,
                setHasUnsavedChanges,
                KYCEmail,
                setKYCEmail,
                disableManagerTypeBtn,
                setDisableManagerTypeBtn,
                disableCandidateTypeBtn,
                setDisableCandidateTypeBtn,
                rerenderKey,
                setRerenderKey,
                candidateId: candidateId || '',
                managerId: managerId || '',
                setActiveTab: (tab) => {
                    setActiveTab(tab);
                },
                handleSelect: (key: string, key2: string, value: any) => {
                    setActiveTab(key);
                    setCandidateType(key2);
                    setKYCEmail(value);
                    setData(value);
                },
                resetAllState,
            }}
        >
            {children}
        </RegistrationContext.Provider>
    );
};
